/**
 * Execution order for one section, as a list and as a graph.
 *
 * The order items run in is not the order they are listed in. `order` is a hint; prerequisites win,
 * and the compile resolves them transitively - so a section whose list reads 1..9 can run in four
 * waves with two items in the third. That difference is invisible in a table, and it is exactly what
 * an author needs to see before deciding whether a step will have what it needs by the time it runs.
 *
 * Two views because they answer different questions. The list answers "what runs when", in waves, and
 * is the one to read when something ran too early. The graph answers "what does this depend on", and
 * is the one to read when deciding whether a prerequisite can be removed. Neither is a superset of
 * the other, which is why the spec asks for both rather than picking one.
 *
 * Cycles are reported here rather than only refused at save time. A cycle cannot be laid out - there
 * is no first wave - so instead of drawing something misleading, the closing path is named: the two
 * items that disagree, in the order they point at each other.
 */

import React, { useMemo, useState } from 'react';
import dagre from 'dagre';
import {
  LabConfigurationItem,
  LabScenarioItem,
  labItemOperationLabel,
  labPrerequisiteCycle,
} from '../../support/labService';
import { LabAuthoringStatus, LabDependencyMark, LabStatusDot, LabStatusLegend } from './LabAdminShared';
import { LabMessage } from './LabShared';

/**
 * Fills for the graph, keyed to the same five statuses the dots use.
 *
 * Hex rather than the Tailwind classes `LabStatusDot` carries, because these are SVG `fill`
 * attributes and a class name means nothing there. Kept beside each other so the two cannot drift
 * into saying different things about the same row.
 */
const NODE_FILL: Record<LabAuthoringStatus, { fill: string; stroke: string; text: string }> = {
  untested: { fill: '#F3F4F6', stroke: '#9CA3AF', text: '#374151' },
  healthy: { fill: '#DCFCE7', stroke: '#16A34A', text: '#14532D' },
  failing: { fill: '#FEE2E2', stroke: '#DC2626', text: '#7F1D1D' },
  running: { fill: '#FEF3C7', stroke: '#F59E0B', text: '#78350F' },
  retired: { fill: '#F9FAFB', stroke: '#D1D5DB', text: '#6B7280' },
};

const NODE_WIDTH = 190;
const NODE_HEIGHT = 56;

export type LabDependencyNode = {
  /** The attachment. The graph is of a section, so a node is an item *as attached here*. */
  attachment: LabScenarioItem;
  item: LabConfigurationItem | null;
  status: LabAuthoringStatus;
  /** Why the status is what it is, for the title text. */
  statusTitle?: string;
};

export type LabDependencyViewProps = {
  scenarioName: string;
  attachments: LabScenarioItem[];
  /** The whole catalogue: prerequisites are item ids, and some of them may not be attached here. */
  catalogue: LabConfigurationItem[];
  /** How each attachment's dot is decided. Defaults to what can be read off the rows. */
  statusOf?: (attachment: LabScenarioItem, item: LabConfigurationItem | null) => {
    status: LabAuthoringStatus;
    title?: string;
  };
  onOpenItem?: (itemId: number) => void;
  className?: string;
};

/**
 * What can be said about an attachment without asking anything else.
 *
 * Deliberately conservative: `healthy` here would claim the item is complete, and this view has not
 * looked at its specs. So the only verdicts are the ones the attachment itself proves - retired,
 * mismatched, or unknown.
 */
const defaultStatus = (
  attachment: LabScenarioItem,
  item: LabConfigurationItem | null,
): { status: LabAuthoringStatus; title?: string } => {
  if (!attachment.enabled) {
    return { status: 'retired', title: 'Detached from the run: still listed, compiles to nothing.' };
  }

  if (item && !item.enabled) {
    return {
      status: 'retired',
      title: 'The item itself is disabled, so this attachment compiles to nothing.',
    };
  }

  if (
    attachment.item_device_type_key &&
    attachment.device_type_key &&
    attachment.item_device_type_key !== attachment.device_type_key
  ) {
    return {
      status: 'failing',
      title: `Written for ${attachment.item_device_type_key} and applied to a ${attachment.device_type_key}.`,
    };
  }

  return { status: 'untested', title: 'Nothing has been compiled from this attachment yet.' };
};

const LabDependencyView: React.FC<LabDependencyViewProps> = ({
  scenarioName,
  attachments,
  catalogue,
  statusOf,
  onOpenItem,
  className = '',
}) => {
  const [mode, setMode] = useState<'list' | 'graph'>('list');

  const byId = useMemo(
    () => new Map(catalogue.map((item) => [item.id, item])),
    [catalogue],
  );

  const nodes = useMemo<LabDependencyNode[]>(
    () =>
      attachments.map((attachment) => {
        const item = byId.get(attachment.item) || null;
        const verdict = (statusOf || defaultStatus)(attachment, item);

        return { attachment, item, status: verdict.status, statusTitle: verdict.title };
      }),
    [attachments, byId, statusOf],
  );

  /** Item ids present in this section, so a prerequisite from outside it can be told apart. */
  const present = useMemo(
    () => new Set(attachments.map((attachment) => attachment.item)),
    [attachments],
  );

  /**
   * Prerequisite edges, split by whether the other end is here.
   *
   * A prerequisite that is not attached is not an edge - it is a gap, and it is the more urgent of
   * the two: the attach pass is supposed to pull the chain in, so one missing means somebody attached
   * by hand or detached a row the chain still needs.
   */
  const { edges, missing } = useMemo(() => {
    const inside: { from: number; to: number }[] = [];
    const gaps: { item: LabConfigurationItem; needs: LabConfigurationItem | null; needsId: number }[] =
      [];

    nodes.forEach((node) => {
      if (!node.item) return;

      (node.item.prerequisites || []).forEach((prerequisiteId) => {
        if (present.has(prerequisiteId)) {
          inside.push({ from: prerequisiteId, to: node.item!.id });
        } else {
          gaps.push({
            item: node.item!,
            needs: byId.get(prerequisiteId) || null,
            needsId: prerequisiteId,
          });
        }
      });
    });

    return { edges: inside, missing: gaps };
  }, [nodes, present, byId]);

  /**
   * Any cycle among the attached items, as the path that closes it.
   *
   * Each item is asked against its own saved prerequisites, so this reports what is already there
   * rather than a proposed edit. The first one found is enough: a section with a cycle has no order
   * at all, and listing every rotation of the same loop would read as several faults.
   */
  const cycle = useMemo(() => {
    for (const node of nodes) {
      if (!node.item) continue;

      const found = labPrerequisiteCycle(
        node.item.id,
        (node.item.prerequisites || []).filter((id) => present.has(id)),
        catalogue,
      );

      if (found) return found;
    }

    return null;
  }, [nodes, present, catalogue]);

  /**
   * The waves the compile will produce.
   *
   * Kahn's algorithm, with `order` breaking ties inside a wave so the list matches what an author
   * arranged where the prerequisites leave a choice. Everything in one wave can run at once; nothing
   * in wave N depends on anything in wave N or later.
   */
  const waves = useMemo<LabDependencyNode[][]>(() => {
    if (cycle) return [];

    const remaining = new Map(nodes.map((node) => [node.attachment.item, node]));
    const incoming = new Map<number, Set<number>>();

    nodes.forEach((node) => incoming.set(node.attachment.item, new Set()));
    edges.forEach((edge) => incoming.get(edge.to)?.add(edge.from));

    const result: LabDependencyNode[][] = [];

    while (remaining.size) {
      const ready = [...remaining.values()]
        .filter((node) => !(incoming.get(node.attachment.item)?.size))
        .sort(
          (left, right) =>
            (left.attachment.order || 0) - (right.attachment.order || 0) ||
            left.attachment.id - right.attachment.id,
        );

      // Only reachable if a cycle slipped past the check above. Stopping beats looping forever.
      if (!ready.length) break;

      ready.forEach((node) => {
        remaining.delete(node.attachment.item);
        incoming.forEach((set) => set.delete(node.attachment.item));
      });

      result.push(ready);
    }

    return result;
  }, [nodes, edges, cycle]);

  /** Laid out by dagre, left to right: a dependency chain reads as a chain that way. */
  const layout = useMemo(() => {
    if (cycle || !nodes.length) return null;

    const graph = new dagre.graphlib.Graph();

    graph.setDefaultEdgeLabel(() => ({}));
    graph.setGraph({ rankdir: 'LR', nodesep: 24, ranksep: 64, marginx: 16, marginy: 16 });

    nodes.forEach((node) =>
      graph.setNode(String(node.attachment.item), { width: NODE_WIDTH, height: NODE_HEIGHT }),
    );
    edges.forEach((edge) => graph.setEdge(String(edge.from), String(edge.to)));

    dagre.layout(graph);

    const placed = nodes.map((node) => {
      const position = graph.node(String(node.attachment.item));

      return {
        node,
        x: (position?.x ?? 0) - NODE_WIDTH / 2,
        y: (position?.y ?? 0) - NODE_HEIGHT / 2,
      };
    });

    const drawn = edges
      .map((edge) => {
        const points = graph.edge(String(edge.from), String(edge.to))?.points || [];

        return { edge, points };
      })
      .filter((entry) => entry.points.length > 1);

    const size = graph.graph();

    return {
      placed,
      drawn,
      width: Math.max(size?.width ?? 0, 320),
      height: Math.max(size?.height ?? 0, 120),
    };
  }, [nodes, edges, cycle]);

  const label = (node: LabDependencyNode) =>
    node.item?.name || node.attachment.item_name || node.attachment.item_key || `item ${node.attachment.item}`;

  const dependsOn = (node: LabDependencyNode) =>
    edges
      .filter((edge) => edge.to === node.attachment.item)
      .map((edge) => byId.get(edge.from)?.name || `item ${edge.from}`);

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="m-0 text-sm font-semibold text-gray-700">Execution order</p>
          <p className="m-0 text-xs text-gray-500">
            What the compile will produce for {scenarioName}. Prerequisites decide this, not the
            positions in the list.
          </p>
        </div>

        {/* Two buttons rather than tabs: neither view is the parent of the other, and a tab strip
            would imply the graph is a detail of the list. */}
        <div className="inline-flex overflow-hidden rounded border border-gray-300">
          {(['list', 'graph'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={`px-3 py-1 text-xs ${
                mode === option
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => setMode(option)}
              aria-pressed={mode === option}
            >
              {option === 'list' ? 'List view' : 'Graph view'}
            </button>
          ))}
        </div>
      </div>

      {/* Before either view, because with a cycle there is no order to show and a diagram drawn
          anyway would be a picture of something that cannot run. */}
      {cycle && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900">
          <p className="m-0 font-semibold">These items wait on each other</p>
          <p className="m-0 mt-1">
            <span className="font-mono">{cycle.join(' → ')}</span>. There is no first step in that
            loop, so nothing in this section can be ordered until one of those prerequisites is
            removed.
          </p>
        </div>
      )}

      {!!missing.length && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <p className="m-0 font-semibold">
            {missing.length === 1
              ? 'One prerequisite is not attached here'
              : `${missing.length} prerequisites are not attached here`}
          </p>
          <ul className="m-0 mt-1 list-disc pl-5">
            {missing.map((gap) => (
              <li key={`${gap.item.id}-${gap.needsId}`}>
                <strong>{gap.item.name}</strong> needs{' '}
                {gap.needs ? gap.needs.name : `item ${gap.needsId}`}, which is not in {scenarioName}.
                The step will be generated and will run without what it depends on.
              </li>
            ))}
          </ul>
        </div>
      )}

      {!nodes.length ? (
        <LabMessage>
          {`${scenarioName} has nothing attached yet, so there is no order to show.`}
        </LabMessage>
      ) : mode === 'list' ? (
        <div className="flex flex-col gap-2">
          {waves.length ? (
            waves.map((wave, index) => (
              <div className="rounded border border-gray-200" key={index}>
                <div className="flex items-baseline justify-between border-b border-gray-100 bg-gray-50 px-3 py-1.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Wave {index + 1}
                  </span>
                  <span className="text-xs text-gray-500">
                    {wave.length === 1
                      ? 'one item'
                      : `${wave.length} items, nothing between them - they may run in any order`}
                  </span>
                </div>

                <ul className="m-0 list-none p-0">
                  {wave.map((node) => {
                    const needs = dependsOn(node);

                    return (
                      <li
                        className="flex items-start gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                        key={node.attachment.id}
                      >
                        <LabStatusDot
                          status={node.status}
                          title={node.statusTitle}
                          className="mt-1.5"
                        />

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline gap-2">
                            {onOpenItem ? (
                              <button
                                type="button"
                                className="border-0 bg-transparent p-0 text-left text-sm font-medium text-blue-700 underline"
                                onClick={() => onOpenItem(node.attachment.item)}
                              >
                                {label(node)}
                              </button>
                            ) : (
                              <span className="font-medium">{label(node)}</span>
                            )}

                            {!!needs.length && <LabDependencyMark names={needs} />}
                          </div>

                          <div className="text-xs text-gray-500">
                            {[
                              node.attachment.device_name || node.attachment.device_key
                                ? `on ${node.attachment.device_name || node.attachment.device_key}`
                                : null,
                              node.item ? labItemOperationLabel(node.item.operation) : null,
                              node.item?.pre_configure ? 'already in the template' : null,
                              node.attachment.added_as_prerequisite
                                ? `pulled in by ${node.attachment.required_by_key || 'another item'}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' • ')}
                          </div>

                          {!!needs.length && (
                            <div className="text-xs text-gray-500">
                              Runs after: {needs.join(', ')}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          ) : (
            <LabMessage tone="warning">
              No order could be worked out, which only happens when items wait on each other. The
              loop is named above.
            </LabMessage>
          )}
        </div>
      ) : layout ? (
        <div className="overflow-auto rounded border border-gray-200 bg-white p-2">
          <svg
            width={layout.width}
            height={layout.height}
            role="img"
            aria-label={`Prerequisite graph for ${scenarioName}`}
          >
            <defs>
              <marker
                id="lab-dep-arrow"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#6B7280" />
              </marker>
            </defs>

            {/* Edges first, so a node never has a line drawn over its text. */}
            {layout.drawn.map((entry) => (
              <polyline
                key={`${entry.edge.from}-${entry.edge.to}`}
                points={entry.points.map((point) => `${point.x},${point.y}`).join(' ')}
                fill="none"
                stroke="#6B7280"
                strokeWidth={1.5}
                markerEnd="url(#lab-dep-arrow)"
              />
            ))}

            {layout.placed.map(({ node, x, y }) => {
              const palette = NODE_FILL[node.status];
              const text = label(node);

              return (
                <g
                  key={node.attachment.id}
                  transform={`translate(${x}, ${y})`}
                  className={onOpenItem ? 'cursor-pointer' : undefined}
                  onClick={onOpenItem ? () => onOpenItem(node.attachment.item) : undefined}
                >
                  <title>
                    {[text, node.statusTitle].filter(Boolean).join(' — ')}
                  </title>

                  <rect
                    width={NODE_WIDTH}
                    height={NODE_HEIGHT}
                    rx={6}
                    fill={palette.fill}
                    stroke={palette.stroke}
                    strokeWidth={1.5}
                  />

                  {/* Truncated by hand: SVG has no text overflow, and a long name would otherwise
                      run out past the box it belongs to. The full one is in the title. */}
                  <text x={10} y={22} fontSize={12} fontWeight={600} fill={palette.text}>
                    {text.length > 26 ? `${text.slice(0, 25)}…` : text}
                  </text>
                  <text x={10} y={40} fontSize={10} fill={palette.text}>
                    {(
                      node.attachment.device_name ||
                      node.attachment.device_key ||
                      'no device'
                    ).slice(0, 30)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      ) : (
        <LabMessage tone="warning">
          The graph cannot be laid out while items wait on each other. The loop is named above.
        </LabMessage>
      )}

      <LabStatusLegend />
    </div>
  );
};

export default LabDependencyView;
